import { state } from './state.js';
import * as API from './api.js';
import { startMonitoringJob, stopMonitoring } from './job_events.js';
import { clearJobState, emitJobEvent, getCurrentJobId, handleProgress, setCancelled, setCurrentJobId } from './job_state.js';
import { setOriginalSize } from './target_controller.js';
import { JOB_PHASES } from './config/jobPhases.js';
import { UI_COPY } from './config/uiCopy.js';
import { autoCollapseInputCards, refreshSettingsVisibility } from './workflow_selection.js';
import { applyPreviewLayout, clearLivePreviewState, prepareLoadingState } from './result_viewer.js';
import { showToast } from './ui/toast.js';

const PENDING_JOB_STORAGE_KEY = 'mosaic_pending_job_id';

function _isRecentMosaicsLimitError(err) {
    if (!err) return false;
    const status = Number(err.status || 0);
    const message = String(err.message || '');
    return status === 429 && /(recent mosaics|my mosaics)\s+limit reached/i.test(message);
}

function _safeReadPendingJobId() {
    try {
        return String(localStorage.getItem(PENDING_JOB_STORAGE_KEY) || '').trim();
    } catch (e) {
        return '';
    }
}

function _safeWritePendingJobId(jobId) {
    const safe = String(jobId || '').trim();
    if (!safe) return;
    try {
        localStorage.setItem(PENDING_JOB_STORAGE_KEY, safe);
    } catch (e) {}
}

function _safeClearPendingJobId() {
    try {
        localStorage.removeItem(PENDING_JOB_STORAGE_KEY);
    } catch (e) {}
}

export function getPendingJobId() {
    return _safeReadPendingJobId();
}

export function rememberPendingJob(jobId) {
    _safeWritePendingJobId(jobId);
}

export function clearPendingJob(jobId = '') {
    const target = String(jobId || '').trim();
    if (!target) {
        _safeClearPendingJobId();
        return;
    }
    const current = _safeReadPendingJobId();
    if (!current || current === target) {
        _safeClearPendingJobId();
    }
}

// Public API ---------------------------------------------------------------

export async function start(settings, options = {}) {
    const jobId = state.jobId;
    const onAccepted = options && typeof options.onAccepted === 'function' ? options.onAccepted : null;
    let startResponse = null;

    try {
        startResponse = await API.runJob(jobId, settings);
    } catch (e) {
        console.error('Failed to start job:', e);
        const detail = e && e.message ? e.message : UI_COPY.jobActions.startFailed;
        if (_isRecentMosaicsLimitError(e)) {
            const limitMatch = detail.match(/\((\d+)\)/);
            const limitText = limitMatch ? limitMatch[1] : '';
            const userMessage = limitText
                ? `My Mosaics limit reached (${limitText}). Delete an existing mosaic to generate a new one.`
                : 'My Mosaics limit reached. Delete an existing mosaic to generate a new one.';
            showToast('My Mosaics limit', userMessage, 5200);
            API.logEvent('recent_mosaics_hard_blocked', {
                job_id: jobId,
                message: detail,
                user_message: userMessage,
            }).catch(() => {});
        } else {
            showToast('Job error', detail, 4000);
        }
        API.logUiFlow('job', 'start_request', 'error', {
            job_id: jobId,
            error: detail,
        }).catch(() => {});
        return false;
    }

    setCurrentJobId(jobId);
    setCancelled(false);
    state.currentJobStartedAtMs = Date.now();
    state.currentJobFirstProgressAtMs = null;
    rememberPendingJob(jobId);
    API.logUiFlow('job', 'start_request', 'ok', {
        job_id: jobId,
    }).catch(() => {});
    if (onAccepted) {
        try {
            onAccepted();
        } catch (e) {
            console.warn('Failed to apply start UI:', e);
        }
    }
    if (startResponse && startResponse.throttled) {
        const delay = Number(startResponse.throttle_delay_s || 0);
        const delayText = delay > 0 ? `${delay}s` : 'a short delay';
        showToast(
            'Free queue throttled',
            `You have reached the free daily speed threshold. This job was queued with ${delayText} delay.`,
            4200
        );
    }
    emitJobEvent('job:accepted', { jobId });
    emitJobEvent('job:phase', { jobId, phase: JOB_PHASES.RUNNING });
    startMonitoringJob(jobId);
    return true;
}

export async function cancel() {
    const jobId = getCurrentJobId() || state.jobId;
    if (!jobId) return;

    setCancelled(true);
    stopMonitoring();

    try {
        await API.cancelJobById(jobId);
        clearPendingJob(jobId);
        emitJobEvent('job:cancelled', { jobId, message: UI_COPY.jobStatus.cancelledFallback });
    } catch (e) {
        console.error('Cancel failed:', e);
        emitJobEvent('job:error', {
            jobId,
            message: UI_COPY.jobActions.cancelFailed,
            error: String(e)
        });
    }
}

export function dispose() {
    setCancelled(true);
    stopMonitoring();
    clearLivePreviewState();
    clearJobState();
}

export function resetResumeUiToIdle() {
    stopMonitoring();
    clearJobState();
    clearLivePreviewState();
    setCancelled(false);
    state.forceShowSettings = false;
    state.currentJobStartedAtMs = null;
    state.currentJobFirstProgressAtMs = null;

    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.add('hidden');

    const resultCard = document.getElementById('resultCard');
    if (resultCard) resultCard.classList.remove('is-loading');
    const resultLoader = document.getElementById('resultLoader');
    if (resultLoader) resultLoader.classList.add('hidden');

    const stopBtn = document.getElementById('stopBtn');
    if (stopBtn) stopBtn.style.display = 'none';

    const gen = document.getElementById('generateBtn');
    if (gen) gen.disabled = false;
    const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
    if (collapsedGen) collapsedGen.disabled = false;

    const bar = document.getElementById('progressBar');
    if (bar) {
        bar.style.width = '0%';
        bar.className = 'progress-fill';
    }
    const stepLabel = document.getElementById('progressStepLabel');
    if (stepLabel) stepLabel.innerText = 'Step 1/4: Analyzing';
    const stepPct = document.getElementById('progressStepPct');
    if (stepPct) stepPct.innerText = '0%';
    const stepDesc = document.getElementById('progressStepDesc');
    if (stepDesc) stepDesc.innerText = 'Waiting for job to start...';
    refreshSettingsVisibility();
}

// Resume API ---------------------------------------------------------------

function _prepareResumeUi() {
    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.remove('hidden');

    prepareLoadingState(0, 0, { expandViewer: true });

    const gen = document.getElementById('generateBtn');
    if (gen) gen.disabled = true;
    const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
    if (collapsedGen) collapsedGen.disabled = true;

    const stopBtn = document.getElementById('stopBtn');
    if (stopBtn) stopBtn.style.display = 'inline-block';
}

function _applySourcePreview(sourceUrl) {
    if (!sourceUrl) return;

    const preview = document.getElementById('sourcePreview');
    const dropZone = document.getElementById('targetDrop');
    if (preview) {
        preview.onload = () => {
            try {
                setOriginalSize(preview.naturalWidth, preview.naturalHeight);
                applyPreviewLayout(preview.naturalWidth, preview.naturalHeight);
            } catch (e) {}
            preview.classList.remove('img-loading');
            if (dropZone) dropZone.classList.remove('is-loading');
        };
        preview.onerror = () => {
            preview.classList.remove('img-loading');
            if (dropZone) dropZone.classList.remove('is-loading');
        };
        preview.classList.add('img-loading');
        if (dropZone) dropZone.classList.add('is-loading');
        preview.src = sourceUrl;
        preview.classList.remove('hidden');
    }

    const label = document.getElementById('targetDropLabel');
    if (label) label.classList.add('hidden');
    const overlay = document.getElementById('targetDropOverlay');
    if (overlay) {
        overlay.classList.remove('hidden');
        overlay.innerHTML = '<span class="overlay-chip">↻ Change photo</span>';
    }
}

export async function resumeActiveJobIfAny() {
    try {
        const response = await API.getActiveJob();
        if (!response || !response.active || !response.job) {
            resetResumeUiToIdle();
            return false;
        }

        const job = response.job;
        const jobId = job.job_id;
        if (!jobId) return false;

        state.jobId = jobId;
        state.targetJobId = jobId;
        state.currentJobStartedAtMs = Date.now();
        state.currentJobFirstProgressAtMs = null;
        state.lastShownSettings = job.settings || null;
        state.selectedSourcePhotoId = job.source_photo_id || (job.source_url ? `legacy:${jobId}` : null);
        if (state.selectedSourcePhotoId) {
            state.selectedSourcePhoto = {
                id: state.selectedSourcePhotoId,
                upload_job_id: jobId,
                stored_filename: '',
                has_been_used: true,
                full_url: job.source_url || '',
                thumb_url: job.source_url || '',
            };
        }
        if (job.library_id) state.selectedLibraryId = job.library_id;

        let fileName = '';
        if (job.source_url) {
            const sourcePath = String(job.source_url).split('?')[0];
            const parts = sourcePath.split('/');
            fileName = parts[parts.length - 1] || '';
            state.targetFilename = fileName;
        }

        _prepareResumeUi();
        autoCollapseInputCards().catch(() => {});
        _applySourcePreview(job.source_url);
        refreshSettingsVisibility();

        setCurrentJobId(jobId);
        setCancelled(false);
        rememberPendingJob(jobId);
        handleProgress(jobId, job, { suppressBuildToast: true });
        startMonitoringJob(jobId, { suppressBuildToast: true });
        console.log(`[Job] Resumed monitoring for active job ${jobId}`);
        return true;
    } catch (e) {
        console.warn('Active job resume check failed:', e);
        resetResumeUiToIdle();
        return false;
    }
}
